import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Query } from '@tanstack/query-core'
import axios from 'axios'
import {
  PATIENT_SUMMARY_ENABLED,
  PatientSummaryRes,
  PatientSummaryShowResponse,
  UpdatePatientSummaryAttrs,
  completePatientSummary,
  generatePatientSummary,
  getPatientSummary,
  getPatientSummaryErrorCode,
  getPortalVisitSummaries,
  getPortalVisitSummary,
  reopenPatientSummary,
  updatePatientSummary
} from '../api/patientSummary'

const POLL_INTERVAL_MS = 3000

export const patientSummaryQueryKey = (encounterId?: string) => ['patient_summary', encounterId]

// Exported (and kept pure) so polling on/off can be asserted without mocking timers: false stops
// react-query's refetch loop, a number (ms) keeps it going.
export const patientSummaryRefetchInterval = (query: Pick<Query<PatientSummaryShowResponse>, 'state'>) =>
  query.state.data?.data.attributes.generation_status === 'generating' ? POLL_INTERVAL_MS : false

const MAX_RETRIES = 3 // react-query's default

// Retrying can't change a 403 (not allowed — e.g. reception asking for a draft) or a disabled
// feature, so those fail at once; anything else (network, 5xx) retries like react-query's default.
export const patientSummaryRetry = (failureCount: number, error: unknown): boolean => {
  if (getPatientSummaryErrorCode(error) === 'patient_summary_disabled') return false
  if (axios.isAxiosError(error) && error.response?.status === 403) return false
  return failureCount < MAX_RETRIES
}

// Puts a saved summary into its own encounter's cache (meta kept) and refetches that encounter only.
const cacheSavedSummary = (queryClient: QueryClient, encounterId: string, saved: PatientSummaryRes) => {
  const queryKey = patientSummaryQueryKey(encounterId)
  queryClient.setQueryData<PatientSummaryShowResponse>(queryKey, old => (old ? { ...old, data: saved } : old))
  void queryClient.invalidateQueries({ queryKey })
}

// An update function bound for good to ONE encounter — for autosaves that may still be pending or
// retried after the screen has moved on to another encounter (the hook's own `update` follows
// whichever encounter the hook currently has, so it must never be used for those). On failure it
// refetches that encounter (e.g. it was completed elsewhere) and rethrows.
export const saveSummaryForEncounter =
  (queryClient: QueryClient, encounterId: string) =>
  async (attrs: UpdatePatientSummaryAttrs): Promise<PatientSummaryRes> => {
    try {
      const saved = await updatePatientSummary(encounterId, attrs)
      cacheSavedSummary(queryClient, encounterId, saved)
      return saved
    } catch (err) {
      void queryClient.invalidateQueries({ queryKey: patientSummaryQueryKey(encounterId) })
      throw err
    }
  }

export type GenerateArgs = { force?: boolean; language?: string | null }

export type GenerateOutcome = { needsConfirm: true } | { needsConfirm: false; summary: PatientSummaryRes }

export const usePatientSummary = (encounterId?: string) => {
  const queryClient = useQueryClient()
  const queryKey = patientSummaryQueryKey(encounterId)
  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const { data, isLoading, isError, error } = useQuery({
    enabled: PATIENT_SUMMARY_ENABLED && !!encounterId,
    queryFn: () => getPatientSummary(encounterId as string),
    queryKey,
    refetchInterval: patientSummaryRefetchInterval,
    retry: patientSummaryRetry
  })

  const { mutateAsync: generate, isPending: isGenerating } = useMutation({
    mutationFn: async ({ force = false, language }: GenerateArgs = {}): Promise<GenerateOutcome> => {
      try {
        const summary = await generatePatientSummary(encounterId as string, force, language)
        return { needsConfirm: false, summary }
      } catch (err) {
        if (getPatientSummaryErrorCode(err) === 'edited_draft') return { needsConfirm: true }
        throw err
      }
    },
    onSuccess: outcome => {
      if (!outcome.needsConfirm) invalidate()
    }
  })

  const { mutateAsync: update, isPending: isUpdating } = useMutation({
    mutationFn: (attrs: UpdatePatientSummaryAttrs) => updatePatientSummary(encounterId as string, attrs),
    // The saved summary goes straight into the cache (meta kept) so a remounted tab sees the doctor's
    // last save at once, without waiting for the refetch.
    onSuccess: saved => cacheSavedSummary(queryClient, encounterId as string, saved)
  })

  const { mutateAsync: complete, isPending: isCompleting } = useMutation({
    mutationFn: () => completePatientSummary(encounterId as string),
    onSuccess: invalidate
  })

  const { mutateAsync: reopen, isPending: isReopening } = useMutation({
    mutationFn: () => reopenPatientSummary(encounterId as string),
    onSuccess: invalidate
  })

  return {
    complete,
    error,
    errorCode: getPatientSummaryErrorCode(error),
    facts: data?.meta.facts,
    generate,
    isCompleting,
    isError,
    isGenerating,
    isLoading,
    isReopening,
    isUpdating,
    latestNoteAt: data?.meta.latest_note_at,
    enabledLanguages: data?.meta.enabled_languages,
    reopen,
    summary: data?.data,
    update
  }
}

// Portal (patient-facing) hooks — completed summaries only.
export const usePortalVisitSummaries = () => {
  const { data, isLoading, isError } = useQuery({
    enabled: PATIENT_SUMMARY_ENABLED,
    queryFn: getPortalVisitSummaries,
    queryKey: ['portal_visit_summaries'],
    retry: patientSummaryRetry
  })

  return { isError, isLoading, summaries: data?.data ?? [] }
}

export const usePortalVisitSummary = (id?: string) => {
  const { data, isLoading, isError } = useQuery({
    enabled: PATIENT_SUMMARY_ENABLED && !!id,
    queryFn: () => getPortalVisitSummary(id as string),
    queryKey: ['portal_visit_summary', id],
    retry: patientSummaryRetry
  })

  return { isError, isLoading, snapshot: data?.meta.snapshot }
}
